import { Schema } from "effect";
import { MessageChannel } from "../models/shopper-ai/conversation/message";

/**
 * Schema definitions for TanStack DB query options format.
 * These schemas match the output of TanStack DB's parseLoadSubsetOptions function.
 *
 * @see https://github.com/tanstack/db/blob/main/docs/reference/functions/parseLoadSubsetOptions.md
 */

export const FieldPath = Schema.Array(
  Schema.Union(Schema.String, Schema.Number),
);
export type FieldPath = typeof FieldPath.Type;

const op = {
  eq: "eq",
  not_eq: "not_eq",
  gt: "gt",
  gte: "gte",
  lt: "lt",
  lte: "lte",
  not_gt: "not_gt",
  not_gte: "not_gte",
  not_lt: "not_lt",
  not_lte: "not_lte",
  in: "in",
  not_in: "not_in",
  all_in: "all_in",
  like: "like",
  ilike: "ilike",
  not_like: "not_like",
  not_ilike: "not_ilike",
  isNull: "isNull",
  not_isNull: "not_isNull",
  isUndefined: "isUndefined",
  not_isUndefined: "not_isUndefined",
  between: "between",
} as const;

export type FilterOperator = (typeof op)[keyof typeof op];

export const EqualityOperator = Schema.Literal(op.eq, op.not_eq);
export type EqualityOperator = typeof EqualityOperator.Type;

export const ArrayOperator = Schema.Literal(op.in, op.not_in);
export type ArrayOperator = typeof ArrayOperator.Type;

export const TagsOperator = Schema.Literal(op.in, op.not_in, op.all_in);
export type TagsOperator = typeof TagsOperator.Type;

export const StatusOperator = Schema.Literal(
  op.eq,
  op.not_eq,
  op.in,
  op.not_in,
);
export type StatusOperator = typeof StatusOperator.Type;

export const StringPatternOperator = Schema.Literal(
  op.like,
  op.ilike,
  op.not_like,
  op.not_ilike,
);
export type StringPatternOperator = typeof StringPatternOperator.Type;

export const NullCheckOperator = Schema.Literal(op.isNull, op.not_isNull);
export type NullCheckOperator = typeof NullCheckOperator.Type;

export const DateComparisonOperator = Schema.Literal(
  op.gte,
  op.lte,
  op.gt,
  op.lt,
  op.between,
);
export type DateComparisonOperator = typeof DateComparisonOperator.Type;

export const DateFilterOperator = Schema.Literal(op.gte, op.lte, op.between);
export type DateFilterOperator = typeof DateFilterOperator.Type;

export const ARRAY_OPERATORS: ArrayOperator[] = [op.in, op.not_in];
export const TAGS_OPERATORS: TagsOperator[] = [op.in, op.not_in, op.all_in];
export const STATUS_OPERATORS: StatusOperator[] = [
  op.eq,
  op.not_eq,
  op.in,
  op.not_in,
];
export const STRING_PATTERN_OPERATORS: StringPatternOperator[] = [
  op.like,
  op.ilike,
  op.not_like,
  op.not_ilike,
];
export const NULL_CHECK_OPERATORS: NullCheckOperator[] = [
  op.isNull,
  op.not_isNull,
];
export const DATE_COMPARISON_OPERATORS: DateComparisonOperator[] = [
  op.gte,
  op.lte,
  op.gt,
  op.lt,
  op.between,
];
export const DATE_FILTER_OPERATORS: DateFilterOperator[] = [
  op.gte,
  op.lte,
  op.between,
];

/**
 * A simple comparison filter matching TanStack DB's SimpleComparison type.
 *
 * Supported operators from TanStack DB:
 * - Comparison: eq, gt, gte, lt, lte, in, like, ilike
 * - Null checking: isNull, isUndefined
 * - NOT-wrapped: not_eq, not_gt, etc. (prefixed with not_)
 *
 * The value is optional for operators like isNull and isUndefined.
 */
export const SimpleFilter = Schema.Struct({
  field: FieldPath,
  operator: Schema.String,
  value: Schema.optional(Schema.Unknown),
});
export type SimpleFilter = typeof SimpleFilter.Type;

//TODO: I don't think we are going to do locale sorting
export const StringSortMethod = Schema.Literal("lexical", "locale");
export type StringSortMethod = typeof StringSortMethod.Type;

export const SortSpec = Schema.Struct({
  field: FieldPath,
  direction: Schema.Literal("asc", "desc"),
  nulls: Schema.Literal("first", "last"),
  stringSort: Schema.optional(StringSortMethod),
  locale: Schema.optional(Schema.String),
  localeOptions: Schema.optional(Schema.Object),
});
export type SortSpec = typeof SortSpec.Type;

export const QueryOptions = Schema.Struct({
  filters: Schema.optionalWith(Schema.Array(SimpleFilter), {
    default: () => [],
  }),
  sorts: Schema.optionalWith(Schema.Array(SortSpec), { default: () => [] }),
  limit: Schema.optional(
    Schema.Number.pipe(
      Schema.int(),
      Schema.positive(),
      Schema.lessThanOrEqualTo(500),
    ),
  ),
});
export type QueryOptions = typeof QueryOptions.Type;

export const ConversationFilterField = {
  STATUS: "status",
  ASSIGNEES: "assignees",
  CHANNELS: "channels",
  CONVERSATION_TAGS: "conversationTags",
  CUSTOMER_TAGS: "customerTags",
  MENTIONS: "mentions",
  WORDS: "words",
  READ_AT: "readAt",
  CREATED_AT: "createdAt",
  CLOSED_AT: "closedAt",
  LAST_RESPONSE_AT: "lastResponseAt",
  LAST_CUSTOMER_MESSAGE_AT: "lastCustomerMessageAt",
  LAST_MERCHANT_MESSAGE_AT: "lastMerchantMessageAt",
} as const;

export type ConversationFilterFieldValue =
  (typeof ConversationFilterField)[keyof typeof ConversationFilterField];

export const TIME_FRAMES = [
  "today",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_year",
  "last_year",
  "custom",
] as const;

export type TimeFrame = (typeof TIME_FRAMES)[number];

export const DateFilterValueSchema = Schema.Struct({
  timeFrame: Schema.Literal(...TIME_FRAMES),
  customDate: Schema.optional(
    Schema.Union(
      Schema.Date,
      Schema.mutable(Schema.Tuple(Schema.Date, Schema.Date)),
    ),
  ),
});
export type DateFilterValue = typeof DateFilterValueSchema.Type;

export type ParsedDateRange =
  | { type: "range"; start: Date; end: Date }
  | { type: "single"; date: Date };

/**
 * Parses a date filter value and returns the actual date range.
 * This handles both predefined time frames and custom date selections.
 *
 * @param value - The filter value, typically containing timeFrame and optionally customDate
 * @param referenceDate - The date to use as "now" for calculating relative ranges (defaults to current date)
 * @returns The parsed date range, or null if the value is invalid
 */
export function parseDateFilterValue(
  value: unknown,
  referenceDate: Date = new Date(),
): ParsedDateRange | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const v = value as Record<string, unknown>;

  if (typeof v.timeFrame !== "string") {
    return null;
  }

  const timeFrame = v.timeFrame as TimeFrame;
  const now = referenceDate;

  if (timeFrame === "custom" && v.customDate) {
    if (Array.isArray(v.customDate) && v.customDate.length === 2) {
      const start = new Date(v.customDate[0] as Date);
      const end = new Date(v.customDate[1] as Date);
      return { type: "range", start, end };
    }
    return { type: "single", date: new Date(v.customDate as Date) };
  }

  const range = getDateRangeFromTimeFrame(timeFrame, now);
  if (range) {
    return { type: "range", start: range.start, end: range.end };
  }

  return null;
}

/**
 * Gets the date range for a predefined time frame.
 *
 * @param timeFrame - The time frame preset
 * @param referenceDate - The date to use as "now" for calculating relative ranges
 * @returns The date range, or null for "custom" or invalid time frames
 */
export function getDateRangeFromTimeFrame(
  timeFrame: TimeFrame,
  referenceDate: Date = new Date(),
): { start: Date; end: Date } | null {
  const now = referenceDate;

  switch (timeFrame) {
    case "today": {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "this_week": {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "last_week": {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay() - 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "this_month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "last_month": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "this_year": {
      const start = new Date(now.getFullYear(), 0, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now.getFullYear(), 11, 31);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "last_year": {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "custom":
      return null;
    default:
      return null;
  }
}

export const StatusFilter = Schema.Struct({
  kind: Schema.Literal("status"),
  operator: StatusOperator,
  value: Schema.Union(Schema.String, Schema.Array(Schema.String)),
});
export type StatusFilter = typeof StatusFilter.Type;

export const AssigneesFilter = Schema.Struct({
  kind: Schema.Literal("assignees"),
  operator: ArrayOperator,
  value: Schema.mutable(Schema.Array(Schema.String)),
});
export type AssigneesFilter = typeof AssigneesFilter.Type;

export const ChannelsFilter = Schema.Struct({
  kind: Schema.Literal("channels"),
  operator: ArrayOperator,
  value: Schema.mutable(Schema.Array(MessageChannel)),
});
export type ChannelsFilter = typeof ChannelsFilter.Type;

export const ConversationTagsFilter = Schema.Struct({
  kind: Schema.Literal("conversationTags"),
  operator: TagsOperator,
  value: Schema.mutable(Schema.Array(Schema.String)),
});
export type ConversationTagsFilter = typeof ConversationTagsFilter.Type;

export const CustomerTagsFilter = Schema.Struct({
  kind: Schema.Literal("customerTags"),
  operator: TagsOperator,
  value: Schema.mutable(Schema.Array(Schema.String)),
});
export type CustomerTagsFilter = typeof CustomerTagsFilter.Type;

export const MentionsFilter = Schema.Struct({
  kind: Schema.Literal("mentions"),
  operator: ArrayOperator,
  value: Schema.mutable(Schema.Array(Schema.String)),
});
export type MentionsFilter = typeof MentionsFilter.Type;

export const ReadStatusFilter = Schema.Struct({
  kind: Schema.Literal("readAt"),
  operator: NullCheckOperator,
});
export type ReadStatusFilter = typeof ReadStatusFilter.Type;

export const WordsFilter = Schema.Struct({
  kind: Schema.Literal("words"),
  operator: StringPatternOperator,
  value: Schema.String,
});
export type WordsFilter = typeof WordsFilter.Type;

export const CreatedAtFilter = Schema.Struct({
  kind: Schema.Literal("createdAt"),
  operator: DateFilterOperator,
  value: DateFilterValueSchema,
});
export type CreatedAtFilter = typeof CreatedAtFilter.Type;

export const ClosedAtFilter = Schema.Struct({
  kind: Schema.Literal("closedAt"),
  operator: DateFilterOperator,
  value: DateFilterValueSchema,
});
export type ClosedAtFilter = typeof ClosedAtFilter.Type;

export const LastResponseAtFilter = Schema.Struct({
  kind: Schema.Literal("lastResponseAt"),
  operator: DateFilterOperator,
  value: DateFilterValueSchema,
});
export type LastResponseAtFilter = typeof LastResponseAtFilter.Type;

export const LastCustomerMessageAtFilter = Schema.Struct({
  kind: Schema.Literal("lastCustomerMessageAt"),
  operator: DateFilterOperator,
  value: DateFilterValueSchema,
});
export type LastCustomerMessageAtFilter =
  typeof LastCustomerMessageAtFilter.Type;

export const LastMerchantMessageAtFilter = Schema.Struct({
  kind: Schema.Literal("lastMerchantMessageAt"),
  operator: DateFilterOperator,
  value: DateFilterValueSchema,
});
export type LastMerchantMessageAtFilter =
  typeof LastMerchantMessageAtFilter.Type;

export type DateFilter =
  | CreatedAtFilter
  | ClosedAtFilter
  | LastResponseAtFilter
  | LastCustomerMessageAtFilter
  | LastMerchantMessageAtFilter;

export const ConversationFilter = Schema.Union(
  StatusFilter,
  AssigneesFilter,
  ChannelsFilter,
  ConversationTagsFilter,
  CustomerTagsFilter,
  MentionsFilter,
  ReadStatusFilter,
  WordsFilter,
  CreatedAtFilter,
  ClosedAtFilter,
  LastResponseAtFilter,
  LastCustomerMessageAtFilter,
  LastMerchantMessageAtFilter,
);
export type ConversationFilter = typeof ConversationFilter.Type;

const SortDirection = Schema.Literal("asc", "desc");
const SortNulls = Schema.Literal("first", "last");

export const CreatedAtSort = Schema.Struct({
  kind: Schema.Literal("createdAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type CreatedAtSort = typeof CreatedAtSort.Type;

export const ClosedAtSort = Schema.Struct({
  kind: Schema.Literal("closedAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type ClosedAtSort = typeof ClosedAtSort.Type;

export const LastResponseAtSort = Schema.Struct({
  kind: Schema.Literal("lastResponseAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type LastResponseAtSort = typeof LastResponseAtSort.Type;

export const LastCustomerMessageAtSort = Schema.Struct({
  kind: Schema.Literal("lastCustomerMessageAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type LastCustomerMessageAtSort = typeof LastCustomerMessageAtSort.Type;

export const LastMerchantMessageAtSort = Schema.Struct({
  kind: Schema.Literal("lastMerchantMessageAt"),
  direction: SortDirection,
  nulls: SortNulls,
});
export type LastMerchantMessageAtSort = typeof LastMerchantMessageAtSort.Type;

export const ConversationSort = Schema.Union(
  CreatedAtSort,
  ClosedAtSort,
  LastResponseAtSort,
  LastCustomerMessageAtSort,
  LastMerchantMessageAtSort,
);
export type ConversationSort = typeof ConversationSort.Type;

export const ConversationQueryOptions = Schema.Struct({
  filters: Schema.Array(ConversationFilter),
  sorts: Schema.Array(ConversationSort),
  limit: Schema.optional(
    Schema.Number.pipe(
      Schema.int(),
      Schema.positive(),
      Schema.lessThanOrEqualTo(500),
    ),
  ),
});
export type ConversationQueryOptions = typeof ConversationQueryOptions.Type;

/**
 * Input type for parseConversationQueryOptions, matching the output of
 * TanStack DB's parseLoadSubsetOptions function.
 */
export interface ParsedLoadSubsetOptions {
  filters: readonly {
    readonly field: readonly (string | number)[];
    readonly operator: string;
    readonly value?: unknown;
  }[];
  sorts: readonly {
    readonly field: readonly (string | number)[];
    readonly direction: "asc" | "desc";
    readonly nulls: "first" | "last";
    readonly stringSort?: "lexical" | "locale";
    readonly locale?: string;
    readonly localeOptions?: object;
  }[];
  limit?: number;
}

/**
 * Attempts to decode a simple filter into a ConversationFilter using the Effect Schema.
 * Returns the decoded filter if valid, or null if decoding fails.
 */
function tryDecodeFilter(filter: {
  readonly field: readonly (string | number)[];
  readonly operator: string;
  readonly value?: unknown;
}): ConversationFilter | null {
  const fieldName = filter.field[0];
  if (typeof fieldName !== "string") {
    return null;
  }

  // Build the object with kind instead of field
  const filterWithKind = {
    kind: fieldName,
    operator: filter.operator,
    ...(filter.value !== undefined && { value: filter.value }),
  };

  // Use Effect Schema to decode and validate
  const result = Schema.decodeUnknownEither(ConversationFilter)(filterWithKind);
  if (result._tag === "Right") {
    return result.right;
  }
  return null;
}

/**
 * Attempts to decode a sort spec into a ConversationSort using the Effect Schema.
 * Returns the decoded sort if valid, or null if decoding fails.
 */
function trySortDecode(sort: {
  readonly field: readonly (string | number)[];
  readonly direction: "asc" | "desc";
  readonly nulls: "first" | "last";
}): ConversationSort | null {
  const fieldName = sort.field[0];
  if (typeof fieldName !== "string") {
    return null;
  }

  // Build the object with kind instead of field
  const sortWithKind = {
    kind: fieldName,
    direction: sort.direction,
    nulls: sort.nulls,
  };

  // Use Effect Schema to decode and validate
  const result = Schema.decodeUnknownEither(ConversationSort)(sortWithKind);
  if (result._tag === "Right") {
    return result.right;
  }
  return null;
}

/**
 * Converts the output of TanStack DB's parseLoadSubsetOptions to ConversationQueryOptions.
 *
 * This function maps the generic field-path based filters and sorts to the
 * type-safe ConversationFilter and ConversationSort discriminated unions
 * using Effect Schema for validation.
 *
 * Filters or sorts that fail schema validation are silently dropped.
 *
 * @param parsed - The output from parseLoadSubsetOptions
 * @returns ConversationQueryOptions with properly typed filters and sorts
 */
export function parseConversationQueryOptions(
  parsed: ParsedLoadSubsetOptions,
): ConversationQueryOptions {
  const filters: ConversationFilter[] = [];
  for (const filter of parsed.filters) {
    const decoded = tryDecodeFilter(filter);
    if (decoded !== null) {
      filters.push(decoded);
    }
  }

  const sorts: ConversationSort[] = [];
  for (const sort of parsed.sorts) {
    const decoded = trySortDecode(sort);
    if (decoded !== null) {
      sorts.push(decoded);
    }
  }

  return { filters, sorts, limit: parsed.limit };
}
